import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Newspaper,
  Compass,
  Users,
  Calendar,
  MessageCircle,
  ShoppingBag,
  BookOpen,
  MapPin,
  User,
  Video,
  GraduationCap,
  LifeBuoy,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Heart,
  Sparkles,
  Contact,
  MoreHorizontal,
  Plus,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToJoinedChatRooms, ChatRoom } from '../../firebase/communityChat';
import { getUserProfileByUid, UserProfile } from '../../firebase/firestore';
import { formatHandle } from '../../utils/formatHandle';
import {
  KIN_CHANGED_EVENT,
  getSavedKin,
  normalizeKinName,
  persistKinSelection
} from '../../utils/kinshipSelection';
import { useTranslation } from 'react-i18next';

interface AppSidebarProps {
  /** Collapsed icon-rail mode (desktop). */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Drawer visibility (mobile). */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  state?: Record<string, unknown>;
  /** Brand entry: rendered in its exact lowercase wordmark form. */
  brand?: boolean;
}

/** Brand mark for the top-of-nav "thekinshipway" item (same logo as the header). */
const LogoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img src="/Official Logo (no background).PNG" alt="" className={`${className || ''} object-contain`} />
);

const KINSHIP_GROUPS = [
  'yellow thunderbirds',
  'red thunderbirds',
  'black thunderbirds',
  'white thunderbirds',
  'yellow oak tree',
  'red oak tree',
  'black oak tree',
  'white oak tree',
  'yellow whale',
  'red whale',
  'black whale',
  'white whale'
];

const getColorDot = (groupName: string) => {
  const name = groupName.toLowerCase();
  if (name.includes('yellow')) return 'bg-yellow-400';
  if (name.includes('red')) return 'bg-red-500';
  if (name.includes('black')) return 'bg-gray-800';
  if (name.includes('white')) return 'bg-white border border-gray-300';
  return 'bg-gray-500';
};

/** Some locale strings arrive ALL-CAPS; render them as Title Case for the clean look. */
const displayLabel = (label: string) =>
  label === label.toUpperCase() ? label.toLowerCase() : label;

const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedKinshipGroup, setSelectedKinshipGroup] = useState<string>('');
  const [joinedSpaces, setJoinedSpaces] = useState<ChatRoom[]>([]);
  const [spacesOpen, setSpacesOpen] = useState(false);
  const [circleOpen, setCircleOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const isActive = (path: string) => location.pathname === path;

  // Same behavior as the previous navigation: selecting a kinship group is
  // persisted (now also to the member's profile) and unlocks the learning
  // journey sections.
  const handleKinshipGroupClick = (groupName: string) => {
    setSelectedKinshipGroup(groupName);
    persistKinSelection(groupName, user?.uid);
    if (location.pathname !== '/cultural-posts' && location.pathname !== '/learning-hub') {
      navigate('/cultural-posts');
    }
  };

  const handleNoKinClick = () => {
    setSelectedKinshipGroup('');
    persistKinSelection(null, user?.uid);
    if (location.pathname === '/cultural-posts' || location.pathname === '/learning-hub') {
      navigate('/');
    }
  };

  useEffect(() => {
    const saved = getSavedKin();
    if (saved) setSelectedKinshipGroup(saved);

    // Stay in sync when the kin is picked/spun elsewhere (Pick Your Kin page,
    // signup wheel) — the sidebar stays mounted across routes.
    const handleKinChange = (event: Event) => {
      setSelectedKinshipGroup((event as CustomEvent<string | null>).detail ?? '');
    };
    window.addEventListener(KIN_CHANGED_EVENT, handleKinChange);
    return () => window.removeEventListener(KIN_CHANGED_EVENT, handleKinChange);
  }, []);

  useEffect(() => {
    if (!user) {
      setJoinedSpaces([]);
      setProfile(null);
      return;
    }
    const unsubscribe = subscribeToJoinedChatRooms(user.uid, setJoinedSpaces);
    getUserProfileByUid(user.uid)
      .then((loadedProfile) => {
        setProfile(loadedProfile);
        // Cross-device memory: if this browser has no kin saved but the
        // member's profile does, restore it locally.
        const profileKin = normalizeKinName(loadedProfile?.kinshipGroup);
        if (profileKin && !getSavedKin()) {
          try {
            localStorage.setItem('selectedKinshipGroup', profileKin);
          } catch {
            /* private mode */
          }
          setSelectedKinshipGroup(profileKin);
        }
      })
      .catch((error) => console.error('Error loading sidebar profile:', error));
    return () => unsubscribe();
  }, [user]);

  // Close the mobile drawer whenever navigation happens.
  useEffect(() => {
    onCloseMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const primaryNav: NavItem[] = [
    { to: '/home', label: 'thekinshipway', icon: LogoIcon, brand: true },
    { to: '/feed', label: 'Feed', icon: Newspaper },
    { to: '/global-feed', label: 'Explore', icon: Compass },
    { to: '/community', label: 'Communities', icon: Users },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/earn-rewards', label: 'Earn Rewards', icon: Sparkles },
    { to: '/messages', label: 'Messages', icon: MessageCircle },
    { to: '/community-members', label: 'People', icon: Contact },
    { to: '/marketplace', label: t('sidebar.marketplace'), icon: ShoppingBag },
    { to: '/profile', label: t('sidebar.profile'), icon: User }
  ];

  const learnNav: NavItem[] = [
    { to: '/kinship-way', label: t('sidebar.livingKinshipWay'), icon: Sparkles },
    { to: '/learning-hub', label: t('sidebar.kinshipLearningJourney'), icon: GraduationCap },
    { to: '/cultural-posts', label: t('sidebar.selfGuidedLearning'), icon: BookOpen }
  ];

  const moreNav: NavItem[] = [
    { to: '/welcome', label: 'Welcome & First Steps', icon: Heart },
    { to: '/pick-your-kin', label: 'Pick Your Kin', icon: Sparkles },
    { to: '/nearby', label: 'Nearby Members', icon: MapPin },
    {
      to: '/community',
      label: t('sidebar.weeklyMeeting'),
      icon: Video,
      state: { clearTopicAndScrollWeekly: true }
    },
    { to: '/tribes', label: t('sidebar.placeBasedHubs'), icon: Compass },
    { to: '/social', label: t('sidebar.social'), icon: Contact }
  ];

  const renderItem = (item: NavItem, subtle = false) => {
    const Icon = item.icon;
    // "/" serves the feed for signed-in members and the landing page for guests,
    // so keep the matching nav item highlighted when the reader is at the root.
    const active =
      isActive(item.to) ||
      (item.to === '/feed' && isActive('/') && Boolean(user)) ||
      (item.to === '/home' && isActive('/') && !user);
    return (
      <Link
        key={`${item.to}-${item.label}`}
        to={item.to}
        state={item.state}
        title={collapsed ? displayLabel(item.label) : undefined}
        className={`group relative flex items-center rounded-xl font-medium capitalize transition-all duration-200 ease-premium ${
          collapsed ? 'justify-center px-0 py-3' : 'gap-3.5 px-3.5 py-2.5'
        } ${subtle ? 'text-[13px]' : 'text-[15px]'} ${
          active
            ? 'bg-accent-blue/10 font-semibold text-accent-blue'
            : 'text-text-secondary hover:bg-surface-sunken hover:text-text-primary'
        }`}
      >
        <Icon
          className={`${subtle ? 'h-[17px] w-[17px]' : 'h-5 w-5'} flex-shrink-0 transition-transform duration-200 ease-premium group-hover:scale-110 ${
            active ? 'text-accent-blue' : ''
          }`}
        />
        {!collapsed && (
          <span className={`min-w-0 flex-1 truncate leading-snug ${item.brand ? 'normal-case font-bold tracking-tight' : ''}`}>
            {item.brand ? item.label : displayLabel(item.label)}
          </span>
        )}
      </Link>
    );
  };

  const sidebarBody = (
    <>
      {/* Slim control row (brand lives in the header, like the reference) */}
      <div className={`flex items-center px-3 pt-3 ${collapsed ? 'justify-center' : 'justify-end'}`}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden lg:grid h-8 w-8 place-items-center rounded-lg text-text-muted transition-colors hover:bg-surface-sunken hover:text-text-primary"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={onCloseMobile}
          aria-label="Close menu"
          className="grid h-8 w-8 place-items-center rounded-lg text-text-muted transition-colors hover:bg-surface-sunken hover:text-text-primary lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable nav */}
      <div className={`sidebar-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4 pt-1 ${collapsed ? 'px-2' : 'px-3'}`}>
        <div className="space-y-1">{primaryNav.map((item) => renderItem(item))}</div>

        {/* Learning journey — unlocked by choosing a kinship circle (existing behavior) */}
        {selectedKinshipGroup && (
          <div className="mt-1 space-y-1 border-t border-dark-border/50 pt-2">
            {learnNav.map((item) => renderItem(item))}
          </div>
        )}

        {/* More */}
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            title={collapsed ? 'More' : undefined}
            className={`flex w-full items-center rounded-xl text-[15px] font-medium text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary ${
              collapsed ? 'justify-center px-0 py-3' : 'gap-3.5 px-3.5 py-2.5'
            }`}
          >
            <MoreHorizontal className="h-5 w-5 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">More</span>
                {moreOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </>
            )}
          </button>
          {moreOpen && (
            <div className={`space-y-0.5 ${collapsed ? '' : 'ml-2 border-l border-dark-border/60 pl-2'}`}>
              {moreNav.map((item) => renderItem(item, true))}
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-help-guide'))}
                title={collapsed ? t('sidebar.helpGuide') : undefined}
                className={`flex w-full items-center rounded-xl text-[13px] font-medium text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary ${
                  collapsed ? 'justify-center px-0 py-3' : 'gap-3.5 px-3.5 py-2.5'
                }`}
              >
                <LifeBuoy className="h-[17px] w-[17px] flex-shrink-0" />
                {!collapsed && <span className="flex-1 text-left">{displayLabel(t('sidebar.helpGuide'))}</span>}
              </button>
            </div>
          )}
        </div>

        {/* Create Post */}
        <Link
          to={user ? '/cultural-posts' : '/login'}
          title={collapsed ? 'Create Post' : undefined}
          className={`mt-4 flex items-center justify-center gap-2 rounded-2xl bg-brand-gradient font-semibold text-white shadow-soft transition-all duration-200 ease-premium hover:-translate-y-0.5 hover:shadow-glow-blue active:translate-y-0 ${
            collapsed ? 'h-11 w-11 mx-auto rounded-full' : 'w-full px-4 py-3 text-[15px]'
          }`}
        >
          <Plus className="h-5 w-5" />
          {!collapsed && 'Create Post'}
        </Link>

        {/* Joined spaces */}
        {!collapsed && joinedSpaces.length > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setSpacesOpen((open) => !open)}
              className="flex w-full items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-semibold text-text-muted transition-colors hover:text-text-primary"
            >
              {spacesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              My Spaces
              <span className="ml-auto rounded-full bg-surface-sunken px-2 py-0.5 text-[10px] font-bold text-text-secondary">
                {joinedSpaces.length}
              </span>
            </button>
            {spacesOpen && (
              <div className="ml-4 space-y-0.5 border-l border-dark-border/70 pl-3">
                {joinedSpaces.map((space) => (
                  <button
                    key={space.id}
                    onClick={() => navigate('/community', { state: { selectedSpaceId: space.id } })}
                    className="block w-full truncate rounded-lg px-2.5 py-1.5 text-left text-[13px] text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary"
                  >
                    {space.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Kinship circle picker */}
        {!collapsed && (
          <div className="mt-4 rounded-2xl border border-dark-border/70 bg-surface-sunken/60 p-3">
            <button
              type="button"
              onClick={() => setCircleOpen((open) => !open)}
              className="flex w-full items-center gap-2 text-xs font-semibold capitalize tracking-wide text-text-muted transition-colors hover:text-text-primary"
            >
              {displayLabel(t('sidebar.kinshipGroups'))}
              {circleOpen ? (
                <ChevronDown className="ml-auto h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="ml-auto h-3.5 w-3.5" />
              )}
            </button>
            {selectedKinshipGroup && !circleOpen && (
              <button
                type="button"
                onClick={() => setCircleOpen(true)}
                className="mt-2 flex w-full items-center gap-2 rounded-lg bg-accent-blue/10 px-2.5 py-1.5 text-left text-[13px] font-medium capitalize text-accent-blue"
              >
                <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${getColorDot(selectedKinshipGroup)}`} />
                <span className="truncate">{selectedKinshipGroup}</span>
              </button>
            )}
            {!selectedKinshipGroup && !circleOpen && (
              <Link
                to="/pick-your-kin"
                className="mt-2 flex w-full items-center gap-2 rounded-lg bg-brand-gradient px-2.5 py-2 text-[13px] font-semibold text-white transition-all duration-200 ease-premium hover:shadow-glow-blue"
              >
                <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
                Find your kin
              </Link>
            )}
            <Link
              to="/pick-your-kin"
              className="mt-2 block px-2.5 text-[11px] font-semibold text-accent-blue transition-colors hover:text-accent-strong"
            >
              Spin the wheel →
            </Link>
            {circleOpen && (
              <div className="mt-2 max-h-56 space-y-0.5 overflow-y-auto pr-1">
                <button
                  onClick={handleNoKinClick}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors ${
                    !selectedKinshipGroup
                      ? 'bg-accent-blue text-white'
                      : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary'
                  }`}
                >
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-gray-500" />
                  <span className="truncate">{t('sidebar.noKin')}</span>
                </button>
                {KINSHIP_GROUPS.map((group) => (
                  <button
                    key={group}
                    onClick={() => handleKinshipGroupClick(group)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] capitalize transition-colors ${
                      selectedKinshipGroup === group
                        ? 'bg-accent-blue text-white'
                        : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary'
                    }`}
                  >
                    <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${getColorDot(group)}`} />
                    <span className="truncate">{group}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer: profile mini-card + legal */}
      <div className={`border-t border-dark-border/60 px-3 py-3 ${collapsed ? 'px-2' : ''}`}>
        {user ? (
          <Link
            to="/profile"
            title={collapsed ? profile?.displayName || 'Profile' : undefined}
            className={`flex items-center rounded-2xl transition-colors hover:bg-surface-sunken ${
              collapsed ? 'justify-center p-1.5' : 'gap-3 p-2.5'
            }`}
          >
            {profile?.profileImageUrl ? (
              <img
                src={profile.profileImageUrl}
                alt=""
                className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-accent-blue/20"
              />
            ) : (
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-accent-blue/15">
                <User className="h-4 w-4 text-accent-blue" />
              </span>
            )}
            {!collapsed && (
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-text-primary">
                  {profile?.displayName || user.displayName || 'Member'}
                </span>
                {profile?.handle && (
                  <span className="block truncate text-xs text-text-muted">
                    {formatHandle(profile.handle)}
                  </span>
                )}
              </span>
            )}
          </Link>
        ) : (
          !collapsed && (
            <Link
              to="/login"
              className="flex items-center gap-3 rounded-2xl p-2.5 transition-colors hover:bg-surface-sunken"
            >
              <img src="/Official Logo (no background).PNG" alt="" className="h-9 w-9 flex-shrink-0 object-contain" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-text-primary">The Kinship Way</span>
                <span className="block truncate text-xs text-text-muted">Sign in to join the circle</span>
              </span>
            </Link>
          )
        )}
        {!collapsed && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 px-2.5 text-[11px] capitalize text-text-muted">
            <Link to="/about" className="transition-colors hover:text-text-primary">About</Link>
            <Link to="/agreements" className="transition-colors hover:text-text-primary">Agreements</Link>
            <Link to="/support" className="transition-colors hover:text-text-primary">Support</Link>
            <Link to="/contact" className="transition-colors hover:text-text-primary">Contact</Link>
            <Link to="/safety" className="transition-colors hover:text-text-primary">Safety</Link>
            <Link to="/terms" className="transition-colors hover:text-text-primary">Terms</Link>
            <Link to="/privacy" className="transition-colors hover:text-text-primary">Privacy</Link>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-full flex-col bg-dark-sidebar shadow-elevated transition-all duration-300 ease-premium lg:static lg:z-auto lg:translate-x-0 lg:border-r lg:border-dark-border/60 lg:shadow-none ${
          collapsed ? 'lg:w-[76px]' : 'lg:w-[264px]'
        } w-[288px] ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {sidebarBody}
      </aside>
    </>
  );
};

export default AppSidebar;
